import { useSyncExternalStore } from "react";

const INSTALL_FLAG = "__LH_REQUEST_TOO_LARGE_ALERT_INSTALLED__";

// Module-level open state so the fetch hook (outside React) can open the
// RequestTooLargeDialog. Repeat 413s keep the one dialog open instead of
// stacking.
let open = false;
const listeners = new Set();

function setRequestTooLargeOpen(next) {
    if (open === next) return;
    open = next;
    listeners.forEach((listener) => listener());
}

function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useRequestTooLargeAlert() {
    const isOpen = useSyncExternalStore(
        subscribe,
        () => open,
        () => false
    );
    return [isOpen, setRequestTooLargeOpen];
}

function isSameOrigin(input) {
    const raw =
        typeof input === "string" || input instanceof URL
            ? String(input)
            : input instanceof Request
              ? input.url
              : "";
    try {
        return new URL(raw, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * nginx rejects bodies over `client_max_body_size` with a 413 HTML page before
 * the request reaches Django, so no caller gets a useful message. The client
 * talks to the API through hundreds of direct fetch() calls, so hook fetch
 * once and open the alert for every same-origin 413. Third-party fetches
 * (Sentry, PostHog) are ignored. The response is returned untouched.
 */
export function installRequestTooLargeAlert() {
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;
    if (window[INSTALL_FLAG]) return;
    window[INSTALL_FLAG] = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
        const response = await originalFetch(input, init);
        if (response.status === 413 && isSameOrigin(input)) {
            setRequestTooLargeOpen(true);
        }
        return response;
    };
}
